import { useRef, useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { assetUrl } from "@/lib/gallery";

export const LOGO_POSITIONS = [
  ["top-left", "Top left"], ["top-right", "Top right"], ["bottom-left", "Bottom left"], ["bottom-right", "Bottom right"],
];
export const LOGO_SIZES = { small: "8%", medium: "12%", large: "18%" };

export function LogoOverlay({ brand, className = "" }) {
  const src = assetUrl(brand?.logo_url);
  if (!src) return null;
  const pos = brand.logo_position || "top-left";
  const style = { width: LOGO_SIZES[brand.logo_size] || LOGO_SIZES.medium, maxHeight: "16%" };
  const cls = `absolute ${pos.includes("top") ? "top-[4%]" : "bottom-[9%]"} ${pos.includes("left") ? "left-[3%]" : "right-[3%]"} ${className}`;
  return <img src={src} alt="Studio logo" data-testid="slide-logo" className={`${cls} object-contain object-left pointer-events-none`} style={style} />;
}

function readAsDataUrl(file) {
  return new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
}

export function LogoUploader({ logoUrl, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4 MB"); return; }
    setUploading(true);
    try {
      const r = await api.post("/brand-settings/logo", { data_url: await readAsDataUrl(file) });
      onChange(r.data.logo_url);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    setUploading(false);
  };

  const remove = async () => {
    try { await api.delete("/brand-settings/logo"); onChange(null); toast.success("Logo removed"); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="eyebrow mb-2">Company Logo</div>
      <p className="text-xs text-stone-500 mb-4">PNG with transparent background works best. Appears on every presentation slide and in PDF/PPTX exports.</p>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="brand-logo-input" />
      <div className="flex items-center gap-4">
        <div className="w-28 h-20 border border-dashed border-stone-300 bg-white flex items-center justify-center overflow-hidden" data-testid="brand-logo-preview">
          {logoUrl ? <img src={assetUrl(logoUrl)} alt="Logo" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-stone-400 font-mono-lab">NO LOGO</span>}
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="brand-logo-upload-btn" className="inline-flex items-center gap-2 border border-stone-300 px-3 py-2 text-sm hover:border-black disabled:opacity-50"><Upload size={14} /> {uploading ? "Uploading…" : logoUrl ? "Replace logo" : "Upload logo"}</button>
          {logoUrl && <button type="button" onClick={remove} data-testid="brand-logo-remove-btn" className="inline-flex items-center gap-2 text-xs text-stone-500 hover:text-black"><Trash2 size={12} /> Remove</button>}
        </div>
      </div>
    </div>
  );
}

export function LogoPlacement({ brand, onChange }) {
  return (
    <>
      <div>
        <div className="eyebrow mb-2">Logo Position</div>
        <div className="grid grid-cols-2 gap-2">
          {LOGO_POSITIONS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => onChange({ logo_position: v })} data-testid={`brand-logo-pos-${v}`}
              className={`px-3 py-2 text-sm border ${brand.logo_position === v ? "bg-black text-white border-black" : "border-stone-300"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="eyebrow mb-2">Logo Size</div>
        <div className="flex gap-2">
          {Object.keys(LOGO_SIZES).map(v => (
            <button key={v} type="button" onClick={() => onChange({ logo_size: v })} data-testid={`brand-logo-size-${v}`}
              className={`px-4 py-2 text-sm border capitalize ${brand.logo_size === v ? "bg-black text-white border-black" : "border-stone-300"}`}>{v}</button>
          ))}
        </div>
      </div>
    </>
  );
}

export function SlidePreview({ brand }) {
  return (
    <div>
      <div className="eyebrow mb-2">Slide preview</div>
      <div className="relative aspect-[16/9] bg-white border slide-shadow overflow-hidden" style={{ borderColor: "#DCDCD5" }} data-testid="brand-slide-preview">
        <LogoOverlay brand={brand} />
        <div className="absolute inset-0 p-[7%] flex flex-col justify-end">
          <div className="eyebrow mb-1">{brand.studio_name}</div>
          <div className="font-serif-editorial text-2xl leading-tight">Project Title</div>
          <div className="text-[10px] text-stone-400 mt-2 font-mono-lab">{brand.footer}</div>
        </div>
      </div>
      <p className="text-xs text-stone-500 mt-2">Position and size apply after you press Save Settings.</p>
    </div>
  );
}
